import { Image } from 'expo-image';
import { XStack } from 'tamagui';

import PSButton from '@/components/ui/PSButton';
import PSIcon from '@/components/ui/PSIcon';
import { Pressable } from 'react-native';
import PSCircleButton from './ui/PSCircleButton';

/**
 * HomePageHeader — the home screen's top bar (molecule).
 *
 * The PS logo pinned left, an auth-dependent control pinned right, spread apart
 * with `justify="space-between"`. When the user is logged out we show the
 * "Sign up" `PSButton`; once logged in that's swapped for a `Pressable` carrying
 * the account (person) glyph — the same bare-icon control the event page's
 * back/share header uses.
 *
 * Render-only, like `Upsell` and `TicketTier`: the bar doesn't know the auth
 * state, it's *told* it. The screen owns the session and passes `isLoggedIn`
 * plus the two handlers down — the header just wears the matching control and
 * fires the handler. (There's no auth provider in the app yet — it's a
 * `TODO(rehaul)` in `_layout.tsx` — so props are the only source of truth we
 * can rely on today, and the pattern already fits the other molecules.)
 *
 * The home SCREEN renders this as a NATIVE stack header, which spreads its own
 * left/right slots — so the two halves are also exported as `HomeHeaderLeft`
 * (the logo) and `HomeHeaderRight` (the auth control) for `headerLeft`/
 * `headerRight`. This whole `XStack` molecule stays as the single composed
 * version; the screen uses the two halves.
 *
 * The logo is the square 1024×1024 AppIcon, scaled to a header-sized 36×36 box
 * and clipped to a CIRCLE (`borderRadius` = half the size). `contentFit="cover"`
 * fills the box (the source is already square, so nothing is cropped away).
 */
type HomePageHeaderProps = {
  isLoggedIn: boolean;
  /** Fired by the "Sign up" button (logged out). */
  onSignup?: () => void;
  /** Fired by the account button (logged in). */
  onAccount?: () => void;
  isMember?: boolean;
};

const LOGO_SIZE = 25;

/** The logo half — home's `headerLeft`. The AppIcon, clipped to a circle. */
export function HomeHeaderLeft({ onPress }: { onPress?: () => void }) {
  return (
    <Pressable onPress={onPress}>
      <Image
        source={require('@/assets/images/ps-logo.png')}
        style={{
          width: LOGO_SIZE,
          height: LOGO_SIZE,
        }}
        contentFit="contain"
      />
    </Pressable>
  );
}

/** The auth-control half — home's `headerRight`. */
export function HomeHeaderRight({
  isLoggedIn,
  onSignup,
  onAccount,
  isMember,
}: HomePageHeaderProps) {
  return isLoggedIn ? (
    <PSCircleButton onPress={onAccount} variant="bare">
      <PSIcon name="person-outline" color="$brandSecondary" size={25} />
    </PSCircleButton>
  ) : (
    <PSButton title="LOGIN" onPress={onSignup} variant="toolbar" />
  );
}

export default function HomePageHeader({
  isLoggedIn,
  onSignup,
  onAccount,
}: HomePageHeaderProps) {
  return (
    <XStack
      bg="$color.transparent"
      py="$sm"
      items="center"
      justify="space-between"
    >
      <HomeHeaderLeft />
      <HomeHeaderRight
        isLoggedIn={isLoggedIn}
        onSignup={onSignup}
        onAccount={onAccount}
      />
    </XStack>
  );
}
